import { type ReactElement } from "react";
import type { BrokerAccount } from "@/lib/brokerAccounts";
import { expiresInLabel, untilLabel } from "@/lib/brokerAccounts";

interface Props {
  account: BrokerAccount;
  /** Now() snapshot for the expiry countdown. Passed in so the row
   *  can stay pure and re-render cleanly when the page-level clock
   *  ticks. */
  now: Date;
  busy: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

export function AccountRow({
  account,
  now,
  busy,
  onConnect,
  onDisconnect,
}: Readonly<Props>): ReactElement {
  const connected = account.status === "connected";
  const borderClass = connected ? "border-l-success" : "border-l-warning";
  return (
    <div
      className={"rounded-xl border border-line bg-surface-1 p-3.5 border-l-[3px] " + borderClass}
    >
      <AccountHeader
        account={account}
        busy={busy}
        onConnect={onConnect}
        onDisconnect={onDisconnect}
      />
      <AccountStats account={account} now={now} />
    </div>
  );
}

interface HeaderProps {
  account: BrokerAccount;
  busy: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

function AccountHeader({
  account,
  busy,
  onConnect,
  onDisconnect,
}: Readonly<HeaderProps>): ReactElement {
  const connected = account.status === "connected";
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2.5">
        <Avatar connected={connected} />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-medium">{account.label}</span>
            <StatusPill connected={connected} />
            {connected && account.openPositionCount > 0 && (
              <span className="pill bg-accent-bg text-accent">In trade</span>
            )}
            {connected && account.openPositionCount === 0 && (
              <span className="pill bg-surface-2 text-ink-muted">Idle</span>
            )}
          </div>
          <div className="mt-0.5 font-mono text-xs text-ink-secondary">
            {account.userId ?? "—"}
            {account.displayName !== null && ` · ${account.displayName}`}
          </div>
        </div>
      </div>
      <AccountActions
        connected={connected}
        busy={busy}
        onConnect={onConnect}
        onDisconnect={onDisconnect}
      />
    </div>
  );
}

function Avatar({ connected }: Readonly<{ connected: boolean }>): ReactElement {
  const bg = connected ? "bg-success-bg" : "bg-warning-bg";
  const text = connected ? "text-success" : "text-warning";
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${bg}`}
      aria-hidden
    >
      <i className={`ti ti-user text-base ${text}`} />
    </div>
  );
}

function StatusPill({ connected }: Readonly<{ connected: boolean }>): ReactElement {
  if (connected) {
    return (
      <span className="pill bg-success-bg text-success">
        <i className="ti ti-circle-check text-xs" /> Connected
      </span>
    );
  }
  return (
    <span className="pill bg-warning-bg text-warning">
      <i className="ti ti-plug-off text-xs" /> Not connected
    </span>
  );
}

interface ActionsProps {
  connected: boolean;
  busy: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

function AccountActions({
  connected,
  busy,
  onConnect,
  onDisconnect,
}: Readonly<ActionsProps>): ReactElement {
  if (connected) {
    return (
      <button
        type="button"
        className="btn btn-secondary px-3 py-1 text-xs text-danger"
        onClick={onDisconnect}
        disabled={busy}
      >
        <i className="ti ti-plug-off text-xs" />
        Disconnect
      </button>
    );
  }
  return (
    <button
      type="button"
      className="btn btn-primary px-3 py-1 text-xs"
      onClick={onConnect}
      disabled={busy}
    >
      <i className="ti ti-link text-xs" />
      Connect
    </button>
  );
}

function openPositionsValue(account: BrokerAccount, connected: boolean): string {
  if (!connected) return "—";
  if (account.openPositionCount > 0) return String(account.openPositionCount);
  return "—";
}

function AccountStats({
  account,
  now,
}: Readonly<{ account: BrokerAccount; now: Date }>): ReactElement {
  const connected = account.status === "connected";
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <StatTile label="Token">
        {connected ? (
          <span>
            Valid{" "}
            {account.expiresAt === null ? expiresInLabel(now) : untilLabel(account.expiresAt, now)}
          </span>
        ) : (
          <span className="text-warning">
            {account.broker === "Dhan" ? "Paste a token" : "Expired 06:00"}
          </span>
        )}
      </StatTile>
      <StatTile label="Open positions">{openPositionsValue(account, connected)}</StatTile>
      <StatTile label="Strategies">
        {account.assignedStrategies.length === 0 ? (
          <span className="text-ink-muted">— unassigned</span>
        ) : (
          account.assignedStrategies.join(", ")
        )}
      </StatTile>
      <StatTile label="Account ID">
        <span className="font-mono">{account.userId ?? "—"}</span>
      </StatTile>
    </div>
  );
}

interface StatTileProps {
  label: string;
  children: React.ReactNode;
}

function StatTile({ label, children }: Readonly<StatTileProps>): ReactElement {
  return (
    <div className="rounded-ctl bg-surface-2 p-2 text-xs">
      <div className="text-[10px] uppercase tracking-wider text-ink-secondary">{label}</div>
      <div className="mt-0.5 font-medium text-ink">{children}</div>
    </div>
  );
}
